import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getClient,
  updateClient,
  deleteClient,
  listTasks,
  listProjects,
  listContracts,
  listAssignments,
  listEmployees,
  listEvents,
  type Client,
  type Task,
  type Project,
  type Contract,
  type Assignment,
  type Employee,
  type Event,
} from '../lib/api'
import { HealthBadge, ClientNotesPanel, ClientFilesPanel } from '../components/client'
import { AssigneeControl } from '../components/assignment/AssigneeControl'
import { TaskDetail, type TaskDetailTab } from '../components/task/TaskDetail'
import { NewTaskModal } from '../components/task/NewTaskModal'
import { StatusSelect, DueDateInput, CompleteToggle } from '../components/task/TaskControls'
import { useSession } from '../hooks/useAuth'
import { roleCanManage, roleCanSeeFinancials, roleCanWrite, roleCanSeeContracts, roleCanSeeAll } from '@shared/enums'
import {
  ArrowLeft,
  Building2,
  Globe,
  Calendar,
  Edit3,
  Trash2,
  Save,
  X,
  FileText,
  Users,
  Activity,
  FolderKanban,
  CheckSquare,
  Clock,
  StickyNote,
  Paperclip,
  Plus,
} from 'lucide-react'

type Tab = 'overview' | 'notes' | 'files' | 'projects' | 'tasks' | 'team' | 'contracts' | 'activity'

export function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  const canManage = roleCanManage(role)
  const canWrite = roleCanWrite(role)
  const canSeeFinancials = roleCanSeeFinancials(role)
  // The Team role opens this page as a working hub (LXP-55): overview, notes,
  // files, links, projects and their own tasks. Contracts and client staffing
  // are the commercial relationship, so those tabs — and edit/delete — are
  // admin-only, matching the API.
  const canSeeContracts = roleCanSeeContracts(role)
  const [editing, setEditing] = useState(false)
  const [tab, setTab] = useState<Tab>('overview')
  // The same TaskDetail card the Tasks screen opens — one component, so a task
  // behaves identically wherever the workflow started.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  // "Add task" from this client (LXP-46): the full New task form with the
  // client locked. A fresh task opens on whichever tab holds anything that
  // didn't save — keyed by task so another card never inherits the notice.
  const [addingTask, setAddingTask] = useState(false)
  const [cardOpts, setCardOpts] = useState<{ taskId: string; tab: TaskDetailTab; notice: string | null } | null>(null)
  const currentEmployeeId = (session?.user as { employeeId?: string } | undefined)?.employeeId ?? null

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClient(id!),
    enabled: !!id,
  })

  // Related data queries
  const { data: tasksData } = useQuery({
    queryKey: ['tasks', { clientId: id }],
    queryFn: () => listTasks({ clientId: id! }),
    enabled: !!id,
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { clientId: id }],
    queryFn: () => listProjects({ clientId: id! }),
    enabled: !!id,
  })

  const { data: contractsData } = useQuery({
    queryKey: ['contracts', { clientId: id }],
    queryFn: () => listContracts({ clientId: id! }),
    enabled: !!id && canSeeContracts,
  })

  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments', { entityType: 'client', entityId: id }],
    queryFn: () => listAssignments({ entityType: 'client', entityId: id! }),
    enabled: !!id && canManage,
  })

  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => listEmployees(),
    enabled: !!id,
  })

  const { data: eventsData } = useQuery({
    queryKey: ['events', { entityType: 'client', entityId: id }],
    queryFn: () => listEvents({ entityType: 'client', entityId: id!, limit: 50 }),
    enabled: !!id && tab === 'activity',
  })

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<Client>) => updateClient(id!, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', id] })
      qc.invalidateQueries({ queryKey: ['clients'] })
      setEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteClient(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      navigate('/clients')
    },
  })

  if (isLoading) {
    return (
      <div className="client-detail">
        <div className="client-detail__loading"><div className="loading-spinner" /></div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="client-detail">
        <div className="client-detail__error">
          <p>Client not found.</p>
          <Link to="/clients" className="btn btn-ghost btn-sm"><ArrowLeft size={16} /> Back to clients</Link>
        </div>
      </div>
    )
  }

  const client = data
  const clientTasks = tasksData?.items ?? []
  const clientProjects = projectsData?.items ?? []
  const clientContracts = contractsData?.items ?? []
  const clientAssignments = assignmentsData?.items ?? []
  const employees = employeesData?.items ?? []
  const events = eventsData?.items ?? []

  const initials = client.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w: string) => w[0])
    .join('')
    .toUpperCase()

  // Derive counts for tab badges
  const openTasks = clientTasks.filter((t) => t.status !== 'done').length
  const activeProjects = clientProjects.filter((p) => p.status !== 'complete').length

  const TABS: { key: Tab; label: string; icon: typeof Activity; badge?: number }[] = [
    { key: 'overview', label: 'Overview', icon: Building2 },
    { key: 'notes', label: 'Notes', icon: StickyNote },
    { key: 'files', label: 'Files', icon: Paperclip },
    { key: 'projects', label: 'Projects', icon: FolderKanban, badge: activeProjects },
    { key: 'tasks', label: 'Tasks', icon: CheckSquare, badge: openTasks },
    ...(canManage
      ? [{ key: 'team' as Tab, label: 'Team', icon: Users, badge: clientAssignments.length }]
      : []),
    ...(canSeeContracts
      ? [{ key: 'contracts' as Tab, label: 'Contracts', icon: FileText, badge: clientContracts.length }]
      : []),
    { key: 'activity', label: 'Activity', icon: Clock },
  ]

  return (
    <div className="client-detail page">
      {/* Task detail slide-over — opened from the Tasks tab or the overdue list */}
      {selectedTaskId && (
        <TaskDetail
          key={selectedTaskId}
          taskId={selectedTaskId}
          initialTab={cardOpts?.taskId === selectedTaskId ? cardOpts.tab : 'details'}
          notice={cardOpts?.taskId === selectedTaskId ? cardOpts.notice : null}
          onClose={() => {
            setSelectedTaskId(null)
            setCardOpts(null)
          }}
        />
      )}

      {addingTask && (
        <NewTaskModal
          onClose={() => setAddingTask(false)}
          onCreated={(taskId, opts) => {
            setAddingTask(false)
            setCardOpts({ taskId, ...opts })
            setSelectedTaskId(taskId)
            qc.invalidateQueries({ queryKey: ['tasks', { clientId: id }] })
          }}
          employees={employees}
          canAssign={canWrite}
          lockedClient={{ id: client.id, name: client.name }}
          currentEmployeeId={currentEmployeeId}
          creatorIsLead={!roleCanSeeAll(role)}
        />
      )}

      {/* Back nav */}
      <div className="client-detail__topbar">
        <Link to="/clients" className="client-detail__back">
          <ArrowLeft size={16} /><span>Clients</span>
        </Link>
        {canManage && (
          <div className="client-detail__actions">
            {!editing && (
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
                <Edit3 size={14} /> Edit
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm client-detail__delete-btn"
              onClick={() => {
                if (confirm(`Delete "${client.name}"? This can't be undone.`)) deleteMutation.mutate()
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="client-detail__header">
        <span className="client-avatar client-avatar--lg">{initials}</span>
        <div className="client-detail__header-info">
          <div className="client-detail__name-row">
            <h1 className="client-detail__name">{client.name}</h1>
            <HealthBadge health={client.health} />
          </div>
          {client.vertical && (
            <span className="client-detail__vertical"><Building2 size={14} /> {client.vertical}</span>
          )}
          <div className="client-detail__quick-stats">
            {canSeeContracts && (
              <span className="client-detail__quick-stat">
                <FileText size={13} /> {clientContracts.length} contract{clientContracts.length !== 1 ? 's' : ''}
              </span>
            )}
            <span className="client-detail__quick-stat">
              <FolderKanban size={13} /> {clientProjects.length} project{clientProjects.length !== 1 ? 's' : ''}
            </span>
            <span className="client-detail__quick-stat">
              <CheckSquare size={13} /> {openTasks} open task{openTasks !== 1 ? 's' : ''}
            </span>
            {canSeeFinancials && client.mrr != null && client.mrr > 0 && (
              <span className="client-detail__quick-stat mono">
                ${client.mrr.toLocaleString()}/mo
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="client-detail__tabs">
        {TABS.map(({ key, label, icon: Icon, badge }) => (
          <button
            key={key}
            className={`client-detail__tab${tab === key ? ' client-detail__tab--active' : ''}`}
            onClick={() => setTab(key)}
          >
            <Icon size={15} />
            <span>{label}</span>
            {badge != null && badge > 0 && (
              <span className="client-detail__tab-badge">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {editing && canManage ? (
        <EditForm
          client={client}
          onSave={(patch) => updateMutation.mutate(patch)}
          onCancel={() => setEditing(false)}
          isLoading={updateMutation.isPending}
          canSeeFinancials={canSeeFinancials}
        />
      ) : (
        <div className="client-detail__body">
          {tab === 'overview' && (
            <OverviewTab client={client} projects={clientProjects} tasks={clientTasks} contracts={clientContracts} canSeeFinancials={canSeeFinancials} onTaskClick={setSelectedTaskId} />
          )}
          {tab === 'notes' && (
            <ClientNotesPanel clientId={client.id} canManage={canManage} />
          )}
          {tab === 'files' && (
            <ClientFilesPanel clientId={client.id} canManage={canManage} />
          )}
          {tab === 'projects' && (
            <ProjectsTab projects={clientProjects} />
          )}
          {tab === 'tasks' && (
            <TasksTab
              tasks={clientTasks}
              canWrite={canWrite}
              onTaskClick={setSelectedTaskId}
              onAddTask={canWrite ? () => setAddingTask(true) : undefined}
            />
          )}
          {tab === 'team' && canManage && (
            <TeamTab clientId={client.id} assignments={clientAssignments} employees={employees} canManage={canManage} />
          )}
          {tab === 'contracts' && canSeeContracts && (
            <ContractsTab contracts={clientContracts} canSeeFinancials={canSeeFinancials} />
          )}
          {tab === 'activity' && (
            <ActivityTab events={events} employees={employees} />
          )}
        </div>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * TAB PANELS
 * ═══════════════════════════════════════════════════════════════════ */

function OverviewTab({ client, projects, tasks, contracts, canSeeFinancials, onTaskClick }: { client: Client; projects: Project[]; tasks: Task[]; contracts: Contract[]; canSeeFinancials: boolean; onTaskClick: (taskId: string) => void }) {
  const totalMrr = contracts.filter((c) => c.status === 'active' || c.status === 'renewing').reduce((s, c) => s + (c.mrr ?? 0), 0)
  const openTasks = tasks.filter((t) => t.status !== 'done')
  const overdueTasks = openTasks.filter((t) => t.due && t.due < new Date().toISOString().slice(0, 10))

  return (
    <>
      {/* Key metrics */}
      <div className="client-detail__metrics">
        <MetricCard label="Plan" value={client.plan ?? '—'} icon={<FileText size={16} />} />
        {canSeeFinancials && (
          <MetricCard label="Active MRR" value={totalMrr ? `$${totalMrr.toLocaleString()}` : '—'} icon={<Activity size={16} />} mono />
        )}
        <MetricCard label="Status" value={client.status ?? 'active'} icon={<Calendar size={16} />} />
      </div>

      {/* Details */}
      <section className="client-detail__section">
        <h2 className="client-detail__section-title">Details</h2>
        <div className="client-detail__info-grid">
          {client.website && <InfoRow icon={<Globe size={15} />} label="Website" value={client.website} link />}
          {client.notes && <InfoRow icon={<Calendar size={15} />} label="Notes" value={client.notes} />}
        </div>
      </section>

      {/* Active projects snapshot */}
      {projects.length > 0 && (
        <section className="client-detail__section">
          <h2 className="client-detail__section-title">
            <FolderKanban size={16} /> Active projects
            <span className="client-detail__section-count">{projects.filter((p) => p.status !== 'complete').length}</span>
          </h2>
          <div className="client-detail__mini-list">
            {projects.filter((p) => p.status !== 'complete').slice(0, 5).map((p) => (
              <div key={p.id} className="client-detail__mini-row">
                <StatusDot status={p.status} />
                <span className="client-detail__mini-name">{p.name}</span>
                <span className="client-detail__mini-meta">{p.progress ?? 0}%</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Overdue tasks alert */}
      {overdueTasks.length > 0 && (
        <section className="client-detail__section client-detail__section--warning">
          <h2 className="client-detail__section-title">
            <Clock size={16} /> Overdue tasks
            <span className="client-detail__section-count">{overdueTasks.length}</span>
          </h2>
          <div className="client-detail__mini-list">
            {overdueTasks.slice(0, 5).map((t) => (
              <div
                key={t.id}
                className="client-detail__mini-row client-detail__mini-row--clickable"
                onClick={() => onTaskClick(t.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onTaskClick(t.id) }}
              >
                <PriorityDot priority={t.priority} />
                <span className="client-detail__mini-name">{t.title}</span>
                <span className="client-detail__mini-meta client-detail__mini-meta--danger">Due {t.due}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

function ProjectsTab({ projects }: { projects: Project[] }) {
  if (projects.length === 0) {
    return <p className="client-detail__empty">No projects yet.</p>
  }

  return (
    <div className="client-detail__project-list">
      {projects.map((p) => (
        <div key={p.id} className="client-detail__project-card">
          <div className="client-detail__project-header">
            <StatusDot status={p.status} />
            <span className="client-detail__project-name">{p.name}</span>
            <span className="client-detail__project-status">{p.status.replace('_', ' ')}</span>
          </div>
          <div className="client-detail__project-meta">
            <span className="chip chip-sm">{p.tier}</span>
            {(p.modules as string[]).map((m) => (
              <span key={m} className="chip chip-sm">{m}</span>
            ))}
            {p.start && <span className="text-muted text-xs">Start {p.start}</span>}
            {p.due && <span className="text-muted text-xs">Due {p.due}</span>}
          </div>
          {(p.totalTasks ?? 0) > 0 && (
            <div className="client-detail__project-progress">
              <div className="client-detail__progress-bar">
                <div className="client-detail__progress-fill" style={{ width: `${p.progress ?? 0}%` }} />
              </div>
              <span className="text-muted text-xs mono">{p.doneTasks}/{p.totalTasks} tasks</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function TasksTab({
  tasks,
  canWrite,
  onTaskClick,
  onAddTask,
}: {
  tasks: Task[]
  canWrite: boolean
  onTaskClick: (taskId: string) => void
  /** Opens the New task form with this client locked (LXP-46). Absent for read-only roles. */
  onAddTask?: () => void
}) {
  const addButton = onAddTask && (
    <div className="client-detail__tasks-toolbar">
      <button type="button" className="btn btn-primary btn-sm" onClick={onAddTask}>
        <Plus size={14} /> Add task
      </button>
    </div>
  )

  if (tasks.length === 0) {
    return (
      <>
        {addButton}
        <p className="client-detail__empty">No tasks yet.</p>
      </>
    )
  }

  const grouped = {
    todo: tasks.filter((t) => t.status === 'todo'),
    in_progress: tasks.filter((t) => t.status === 'in_progress'),
    review: tasks.filter((t) => t.status === 'review'),
    blocked: tasks.filter((t) => t.status === 'blocked'),
    done: tasks.filter((t) => t.status === 'done'),
  }

  const STATUS_META: Record<string, { label: string; color: string }> = {
    todo: { label: 'To do', color: 'var(--text-secondary)' },
    in_progress: { label: 'In progress', color: 'var(--accent-primary)' },
    review: { label: 'Review', color: 'var(--state-warning)' },
    blocked: { label: 'Blocked', color: 'var(--state-danger)' },
    done: { label: 'Done', color: 'var(--state-success)' },
  }

  return (
    <>
      {addButton}
    <div className="client-detail__task-groups">
      {Object.entries(grouped).filter(([, list]) => list.length > 0).map(([status, list]) => {
        const meta = STATUS_META[status] ?? { label: status, color: 'var(--text-secondary)' }
        return (
          <div key={status} className="client-detail__task-group">
            <div className="client-detail__task-group-header">
              <span className="client-detail__task-group-dot" style={{ background: meta.color }} />
              <span>{meta.label}</span>
              <span className="client-detail__section-count">{list.length}</span>
            </div>
            {list.map((t) => (
              <div
                key={t.id}
                className="client-detail__task-row client-detail__task-row--clickable"
              >
                <CompleteToggle task={t} disabled={!canWrite} size={16} className="task-check-inline" />
                <PriorityDot priority={t.priority} />
                {/* The title is the button, not the row. Making the row itself
                    `role="button"` would nest the status select and date input
                    inside a button — invalid, and it swallows their keyboard
                    events. Same split as the task list. */}
                <span
                  className="client-detail__task-title"
                  onClick={() => onTaskClick(t.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onTaskClick(t.id) }}
                >
                  {t.title}
                </span>
                <span className="chip chip-sm">{t.discipline}</span>
                <StatusSelect task={t} disabled={!canWrite} className="status-select--sm" />
                <DueDateInput task={t} disabled={!canWrite} className="due-input--sm" />
              </div>
            ))}
          </div>
        )
      })}
    </div>
    </>
  )
}

function TeamTab({
  clientId,
  assignments,
  employees,
  canManage,
}: {
  clientId: string
  assignments: Assignment[]
  employees: Employee[]
  canManage: boolean
}) {
  return (
    <div className="client-detail__team">
      <AssigneeControl
        entityType="client"
        entityId={clientId}
        assignments={assignments}
        employees={employees}
        canManage={canManage}
      />
      {!canManage && assignments.length === 0 && (
        <p className="client-detail__empty">No team members assigned yet.</p>
      )}
    </div>
  )
}

function ContractsTab({ contracts, canSeeFinancials }: { contracts: Contract[]; canSeeFinancials: boolean }) {
  if (contracts.length === 0) {
    return <p className="client-detail__empty">No contracts yet.</p>
  }

  const STATUS_TONE: Record<string, string> = {
    draft: 'var(--text-secondary)',
    active: 'var(--state-success)',
    renewing: 'var(--state-warning)',
    expired: 'var(--state-danger)',
    cancelled: 'var(--text-secondary)',
  }

  return (
    <div className="client-detail__contract-list">
      {contracts.map((c) => (
        <div key={c.id} className="client-detail__contract-card">
          <div className="client-detail__contract-header">
            <FileText size={16} style={{ color: 'var(--accent-primary)' }} />
            <span className="client-detail__contract-name">{c.name}</span>
            <span
              className="client-detail__contract-status"
              style={{ color: STATUS_TONE[c.status] ?? 'var(--text-secondary)' }}
            >
              {c.status}
            </span>
          </div>
          <div className="client-detail__contract-meta">
            <span className="chip chip-sm">{c.tier}</span>
            <span className="chip chip-sm">{c.term}</span>
            {canSeeFinancials && c.mrr != null && c.mrr > 0 && <span className="mono text-xs">${c.mrr.toLocaleString()}/mo</span>}
            {c.startDate && <span className="text-muted text-xs">Start {c.startDate}</span>}
            {c.endDate && <span className="text-muted text-xs">End {c.endDate}</span>}
          </div>
          {c.renewalDate && (
            <div className="client-detail__contract-renewal">
              <Clock size={12} /> Renewal {c.renewalDate}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ActivityTab({ events, employees }: { events: Event[]; employees: Employee[] }) {
  if (events.length === 0) {
    return <p className="client-detail__empty">No activity recorded yet.</p>
  }

  const empMap = new Map(employees.map((e) => [e.id, e]))
  const sorted = [...events].sort((a, b) => (b.at > a.at ? 1 : -1))

  return (
    <div className="client-detail__timeline">
      {sorted.map((ev, i) => {
        const emp = empMap.get(ev.actor)
        const isLast = i === sorted.length - 1
        return (
          <div key={ev.id} className="client-detail__timeline-item">
            <div className="client-detail__timeline-rail">
              <span className="client-detail__timeline-dot">{emp?.initials ?? 'SY'}</span>
              {!isLast && <span className="client-detail__timeline-line" />}
            </div>
            <div className="client-detail__timeline-content">
              <div className="client-detail__timeline-text">
                <strong>{emp?.name ?? ev.actor}</strong>{' '}
                {formatEventText(ev)}
              </div>
              <div className="client-detail__timeline-time mono">{formatTime(ev.at)}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * SHARED SUB-COMPONENTS
 * ═══════════════════════════════════════════════════════════════════ */

function MetricCard({ label, value, icon, mono }: { label: string; value: string; icon: React.ReactNode; mono?: boolean }) {
  return (
    <div className="client-detail__metric-card">
      <div className="client-detail__metric-icon">{icon}</div>
      <div className="client-detail__metric-content">
        <span className="client-detail__metric-label">{label}</span>
        <span className={`client-detail__metric-value${mono ? ' mono' : ''}`}>{value}</span>
      </div>
    </div>
  )
}

function InfoRow({ icon, label, value, link }: { icon: React.ReactNode; label: string; value: string; link?: boolean }) {
  return (
    <div className="client-detail__info-row">
      <span className="client-detail__info-icon">{icon}</span>
      <span className="client-detail__info-label">{label}</span>
      {link ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="client-detail__info-value client-detail__info-link">{value}</a>
      ) : (
        <span className="client-detail__info-value">{value}</span>
      )}
    </div>
  )
}

function StatusDot({ status }: { status: string }) {
  const COLORS: Record<string, string> = {
    planning: 'var(--accent-primary)',
    active: 'var(--state-success)',
    on_hold: 'var(--state-warning)',
    complete: 'var(--text-secondary)',
  }
  return <span className="priority-dot" style={{ background: COLORS[status] ?? 'var(--text-secondary)' }} />
}

function PriorityDot({ priority }: { priority: string }) {
  return <span className={`priority-dot priority-${priority}`} />
}

function formatEventText(ev: Event): string {
  switch (ev.type) {
    case 'created': return 'created this client'
    case 'status_changed': return `changed status from "${ev.fromState}" to "${ev.toState}"`
    case 'assigned': return `assigned ${(ev.meta as any)?.role ?? 'team member'}`
    case 'unassigned': return `removed assignment`
    case 'field_changed': return `updated ${((ev.meta as any)?.fields as string[])?.join(', ') ?? 'fields'}`
    default: return ev.type.replace(/_/g, ' ')
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/* ═══════════════════════════════════════════════════════════════════
 * EDIT FORM
 * ═══════════════════════════════════════════════════════════════════ */

function EditForm({ client, onSave, onCancel, isLoading, canSeeFinancials }: { client: Client; onSave: (data: Partial<Client>) => void; onCancel: () => void; isLoading: boolean; canSeeFinancials: boolean }) {
  const [name, setName] = useState(client.name)
  const [vertical, setVertical] = useState(client.vertical ?? '')
  const [plan, setPlan] = useState(client.plan ?? '')
  const [health, setHealth] = useState(client.health)
  const [mrr, setMrr] = useState(client.mrr != null ? String(client.mrr) : '')
  const [website, setWebsite] = useState(client.website ?? '')
  const [notes, setNotes] = useState(client.notes ?? '')

  return (
    <form
      className="client-detail__edit-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!name.trim()) return
        onSave({
          name: name.trim(),
          vertical: vertical || null,
          plan: plan || null,
          health,
          mrr: mrr ? Number(mrr) : null,
          website: website || null,
          notes: notes || null,
        })
      }}
    >
      <div className="client-detail__edit-grid">
        <div className="client-detail__field">
          <label>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="client-detail__field">
          <label>Vertical</label>
          <input value={vertical} onChange={(e) => setVertical(e.target.value)} placeholder="e.g. Dental" />
        </div>
        <div className="client-detail__field">
          <label>Plan</label>
          <input value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="e.g. Retainer + Answer" />
        </div>
        <div className="client-detail__field">
          <label>Health</label>
          <select value={health} onChange={(e) => setHealth(e.target.value)}>
            <option value="good">Healthy</option>
            <option value="watch">Watch</option>
            <option value="at-risk">At risk</option>
          </select>
        </div>
        {canSeeFinancials && (
          <div className="client-detail__field">
            <label>MRR ($)</label>
            <input type="number" min={0} value={mrr} onChange={(e) => setMrr(e.target.value)} placeholder="0" />
          </div>
        )}
        <div className="client-detail__field">
          <label>Website</label>
          <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="example.com" />
        </div>
        <div className="client-detail__field client-detail__field--full">
          <label>Notes / Next touchpoint</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Add context…" />
        </div>
      </div>
      <div className="client-detail__edit-actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={isLoading || !name.trim()}>
          <Save size={14} /> {isLoading ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          <X size={14} /> Cancel
        </button>
      </div>
    </form>
  )
}
